import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Box,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  alpha,
  useTheme,
  CircularProgress
} from "@mui/material";
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff
} from "@mui/icons-material";
import { login } from "../api/authApi";
import CustomSnackbarAlert from "../components/customAlert";
import { useUser } from "../context/UserContext";
import { fetchUserProfile } from "../api/usersApi";

const LoginPage = () => {
  const theme = useTheme();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Alert
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [showAlert, setShowAlet] = useState(false);

  const { setUser } = useUser();
  const navigate = useNavigate();

  const handleCloseSnackbar = () => {
    setShowAlet(false);
  };

  const getProfile = async () => {
    const data = await fetchUserProfile((errMessage) => {
      setAlertMessage(errMessage || "An error occurred");
      setAlertSeverity("error");
      setShowAlet(true);
      navigate("/login");
    });
    
    if (data.status) setUser(data.data);
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await login(username, password);
      if (response.success) {
        localStorage.setItem("token", response.token);
        const data = await getProfile();
        if(data.status){
          const userRole = data.data.role;
          if (userRole === "admin") {
            navigate("/adminDashboard");
          } else if (userRole === "manager") {
            navigate("/managerDashboard");
          } else {
            navigate("/userDashboard");
          }
        }
        else{
          setAlertMessage(data.error);
          setAlertSeverity("error");
          setShowAlet(true);
        }
      } else {
        setAlertMessage(response.error || "Login failed");
        setAlertSeverity("error");
        setShowAlet(true);
      }
    } catch (err) {
      setAlertMessage("An error occurred");
      setAlertSeverity("error");
      setShowAlet(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.15)} 100%)`,
        p: 2
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 420,
          width: "100%",
          p: { xs: 3, sm: 5 },
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.25)}`
          }}
        >
          <LockIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>

        <Typography variant="h4" sx={{ mb: 1, textAlign: 'center' }}>
          Welcome back
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
          Please enter your credentials to access the admin portal.
        </Typography>

        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <TextField
            label="Username"
            variant="outlined"
            fullWidth
            required
            margin="normal"
            value={username}
            autoComplete="username"
            onChange={(e) => setUsername(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            required
            margin="normal"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              mt: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 700,
              borderRadius: 2,
              position: 'relative'
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
          </Button>
        </form>

        <Typography variant="caption" color="text.disabled" sx={{ mt: 4 }}>
          &copy; {new Date().getFullYear()} Investobull Fintech. All rights reserved.
        </Typography>
      </Paper>

      <CustomSnackbarAlert
        message={alertMessage}
        severity={alertSeverity}
        open={showAlert}
        onClose={handleCloseSnackbar}
      />
    </Box>
  );
};

export default LoginPage;

